/* Single source of truth for the traffic-source questionnaire.
   Imported by both the primary Publisher onboarding form and the
   per-publisher Offer Application flow so the two never drift apart. */
import {
  Zap,
  Target,
  ShoppingBag,
  Search,
  MapPin,
  BookOpen,
  Sparkles,
} from "lucide-react";

// All 50 US states — used by the GMB Service Areas multi-select.
export const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
];

export const PRIMARY_SOURCES = [
  {
    key: "google_ads",
    label: "Google Ads",
    tagline: "PPC search + display",
    icon: Zap,
    color: "from-amber-500 to-orange-500",
    matchesLegacy: "Google Ads",
    fields: [
      { name: "website_url", label: "Website URL", placeholder: "https://your-landing.com", type: "url", required: true },
      { name: "target_location", label: "Target Location", placeholder: "e.g. Miami FL, or United States (nationwide)", required: true },
      { name: "estimated_calls_per_day", label: "Estimated Calls / Day", placeholder: "e.g. 25", type: "number", required: true },
    ],
    proofSlots: [
      { key: "campaign_homepage", label: "Google Ads Campaign Homepage Screenshot", hint: "Take a screenshot of your Google Ads campaign dashboard homepage" },
      { key: "ad_creative", label: "Google Ad Creative Screenshot", hint: "Take a screenshot of the actual ad content/creative you are running" },
      { key: "keyword_targeting", label: "Keyword Targeting Screenshot", hint: "Take a screenshot of your targeted keywords list" },
    ],
    proofHint: "Upload screenshots of your Ad copies, Keyword lists, and Google Ads Dashboard.",
  },
  {
    key: "meta_ads",
    label: "Meta Ads",
    tagline: "Facebook & Instagram paid",
    icon: Target,
    color: "from-blue-500 to-indigo-500",
    matchesLegacy: "Facebook Ads",
    fields: [
      { name: "fb_page_url", label: "FB Business Page URL / Website URL", placeholder: "https://facebook.com/your-business-page  or  https://your-site.com", type: "url", required: true },
      { name: "target_location", label: "Target Location", placeholder: "e.g. Texas + California, or United States", required: true },
      { name: "estimated_calls_per_day", label: "Estimated Calls / Day", placeholder: "e.g. 25", type: "number", required: true },
    ],
    proofSlots: [
      { key: "campaign_homepage", label: "Meta Ads Campaign Homepage Screenshot", hint: "Take a screenshot of your Meta Ads campaign dashboard homepage" },
      { key: "ad_creative", label: "Meta Ad Creative Screenshot", hint: "Take a screenshot of the actual ad content/creative you are running" },
      { key: "audience_targeting", label: "Audience Targeting Screenshot", hint: "Take a screenshot of your audience targeting settings" },
    ],
    proofHint: "Required: Upload screenshots of your active Meta Ad Campaign Dashboard AND your Live Ad Preview. Applications without both will be rejected.",
  },
  {
    key: "fb_marketplace",
    label: "Facebook Marketplace",
    tagline: "Local listings & inbox",
    icon: ShoppingBag,
    color: "from-cyan-500 to-blue-500",
    matchesLegacy: "Facebook Marketplace",
    fields: [
      { name: "fb_profile_url", label: "Facebook Marketplace Post Link / URL", placeholder: "https://facebook.com/marketplace/item/…", type: "url", required: true },
      { name: "target_location", label: "Target Location", placeholder: "e.g. Miami FL, Tampa FL, Orlando FL", required: true },
      { name: "estimated_calls_per_day", label: "Estimated Calls / Day", placeholder: "e.g. 25", type: "number", required: true },
    ],
    proofHint: "Upload screenshots of your active Marketplace listings and your FB profile history.",
  },
  {
    key: "seo",
    label: "SEO Website",
    tagline: "Owned domains, organic",
    icon: Search,
    color: "from-emerald-500 to-teal-500",
    matchesLegacy: "SEO / Organic Traffic",
    fields: [
      { name: "website_url", label: "Website URL", placeholder: "https://your-site.com", type: "url", required: true },
      { name: "main_service", label: "Main Service / Offer", placeholder: "e.g. HVAC repair, personal injury attorney…", required: true },
      { name: "target_location", label: "Target Location", placeholder: "e.g. Houston TX, or United States", required: true },
      { name: "estimated_calls_per_day", label: "Estimated Calls / Day", placeholder: "e.g. 25", type: "number", required: true },
    ],
    proofHint: "Upload screenshots of your Google Search Console performance and Domain analytics.",
  },
  {
    key: "google_my_business",
    label: "Google My Business (GMB)",
    tagline: "Verified local listings",
    icon: MapPin,
    color: "from-green-500 to-emerald-600",
    matchesLegacy: "Google My Business",
    fields: [
      { name: "gmb_listings_count", label: "Verified Listings Count", placeholder: "e.g. 12", type: "number", required: true },
      { name: "gmb_business_category", label: "Business Category", placeholder: "e.g. HVAC, Plumbing, Legal", required: true },
      { name: "gmb_service_areas", label: "Service Areas", multiSelect: true, options: US_STATES, required: true },
      { name: "gmb_dashboard_proof", label: "Proof of Ownership — Upload GMB Dashboard Screenshot", type: "file", required: true },
      { name: "gmb_profile_link", label: "Example Profile — Link to a Live GMB Profile", placeholder: "https://maps.google.com/…", type: "url", required: true },
    ],
    proofHint: "Upload a screenshot of your GMB Dashboard showing your verified listings.",
  },
  {
    key: "blogs",
    label: "Blog / Content",
    tagline: "Content sites & articles",
    icon: BookOpen,
    color: "from-purple-500 to-fuchsia-500",
    matchesLegacy: "SEO / Organic Traffic",
    fields: [
      { name: "blog_url", label: "Blog URL", placeholder: "https://your-blog.com", type: "url", required: true },
      { name: "example_article_url", label: "Example Article URL", placeholder: "https://your-blog.com/best-plumbers", type: "url", required: true },
      { name: "main_service", label: "Main Service / Offer", placeholder: "e.g. Home services lead-gen articles", required: true },
      { name: "estimated_calls_per_day", label: "Estimated Calls / Day", placeholder: "e.g. 25", type: "number", required: true },
    ],
    proofHint: "Upload an Analytics Screenshot (Google Analytics or GSC) showing traffic.",
  },
  {
    key: "others",
    label: "Other",
    tagline: "Native, YouTube, Email…",
    icon: Sparkles,
    color: "from-slate-500 to-slate-700",
    matchesLegacy: "Other",
    fields: [
      { name: "source_name", label: "Source Name", placeholder: "e.g. TikTok organic, Taboola native, cold-calling…", required: true },
      { name: "website_url", label: "Website URL", placeholder: "https://…", type: "url", required: true },
      { name: "traffic_description", label: "Description of Traffic", placeholder: "How you drive calls — audience, targeting, budget…", required: true, textarea: true },
      { name: "estimated_calls_per_day", label: "Estimated Calls / Day", placeholder: "e.g. 25", type: "number", required: true },
    ],
    proofHint: "Upload your broadcast logs and creative copies.",
  },
];

export const sourceByKey = (key) => PRIMARY_SOURCES.find((s) => s.key === key);

// Proof-of-work file rules — kept in sync with the primary onboarding form.
export const PROOF_MAX_FILES = 3;
export const PROOF_MIN_FILES = 1;
export const PROOF_MAX_BYTES = 10 * 1024 * 1024; // 10 MB per file
export const PROOF_ALLOWED_MIME = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
];
